import { convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';
import { BillingAmount } from '@/components/BillingAmount';
import { Download, FileText } from 'lucide-react';
import type { DeliveryDetailBilling, DeliveryDetailInvoice } from '@/types/deliveryDetail.types';
import { useNavigate } from 'react-router-dom';
import KeyValueListDisplay from '@/components/KeyValueDisplay';
import { downloadDeliveryInvoicePDF } from '@/services/deliveryService';
import { buildInvoiceRoute } from '@/pages/delivery/invoice/invoiceRouteHelpers';
import { getDeliveryInvoiceStatusBadge } from '@/pages/delivery/invoice/deliveryInvoiceStatus';
import { DeliveryInvoiceStatusBadge } from '@/pages/delivery/invoice/DeliveryInvoiceStatusBadge';
import { EmailLogStatusLabel } from '@/utils/constant';
import { EmailStatus } from '@/types/types';

// For intake forms (just billing items)
export const BillingIntakeContent = ({ data }: { data: DeliveryDetailBilling | undefined }) => {
  if (!data) return null;

  return (
    <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm">
      <div className="flex flex-col gap-3 w-full">
        <span className="text-primary font-semibold">Billing Items</span>
        <div className="flex flex-col gap-2">
          {data.billingItems.map((item, index) => (
            <div key={index} className="flex flex-row justify-between items-start text-sm">
              <div className="font-medium flex-shrink-0 w-2/3">{item.description ?? item.code}</div>
              <BillingAmount value={item.amount} className="text-end font-normal w-1/3 break-words" />
            </div>
          ))}
        </div>
      </div>
      <hr />
      <div className="flex flex-row justify-between items-center text-base font-semibold">
        <span>Total Billing Amount</span>
        <BillingAmount value={data.totalBillingAmount} className="text-primary" />
      </div>
    </div>
  );
};

// For work orders (invoice card with email logs)
export const BillingWorkOrderContent = ({
  data,
  workOrderId,
}: {
  data: DeliveryDetailInvoice | undefined;
  workOrderId?: string;
}) => {
  const navigate = useNavigate();
  const emailLogs = data?.lastFiveInvoiceEmails ?? [];
  const invoice = data?.invoice;

  if (!data || !invoice) {
    return (
      <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm">
        <div className="w-full justify-center flex font-medium text-sm">No Billing Data</div>
      </div>
    );
  }

  const statusBadgeConfig = getDeliveryInvoiceStatusBadge(invoice.invoiceStatus);

  return (
    <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm">
      {emailLogs.length > 0 && (
        <div className="flex flex-col gap-3 w-full">
          <span className="text-primary font-semibold">Emails Sent</span>
          <KeyValueListDisplay
            data={emailLogs.map((log) => ({
              label: convertISODateToMMDDYYYY(log.sentAt) + ' ' + convertISOToTime(log.sentAt),
              value: EmailLogStatusLabel[(log.status ?? EmailStatus.SENT) as EmailStatus],
              valueClassName: (log.status ?? EmailStatus.SENT) === EmailStatus.SENT ? 'text-green-700' : 'text-error',
            }))}
          />
        </div>
      )}

      <div
        className="flex flex-row gap-2 justify-between p-4 rounded-lg bg-[#F5F5F5] cursor-pointer"
        onClick={() => {
          navigate(buildInvoiceRoute(invoice.invoiceNumber, 'view', workOrderId));
        }}
      >
        <div className="flex flex-row gap-2">
          <FileText className="w-6 h-6 text-primary shrink-0" />
          <div className="flex flex-col gap-2">
            <span className="font-medium text-sm text-primary">{invoice.invoiceNumber}</span>
            {invoice.modifiedAt && invoice.modifiedBy && (
              <span className="text-xs text-placeholder">{`Updated: ${convertISODateToMMDDYYYY(invoice.modifiedAt)} ${convertISOToTime(invoice.modifiedAt)} by ${invoice.modifiedBy}`}</span>
            )}
            <div className="font-medium">
              <span className="text-placeholder">Amount: </span>
              <BillingAmount value={invoice.totalBillingAmount} className="text-primary" />
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-between gap-2 shrink-0">
          <DeliveryInvoiceStatusBadge status={statusBadgeConfig} />
          <div className="flex items-end justify-end">
            <Download
              className="h-5 w-5 text-primary mr-4 cursor-pointer"
              aria-label="Download invoice PDF"
              onClick={async (e) => {
                e.stopPropagation();
                await downloadDeliveryInvoicePDF(invoice.invoiceNumber);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
